import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { ClientApi } from "../../../apis";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  clientNickname: Yup.string().required("Client nickname is required"),
  company: Yup.string().required("Company is required"),
  contactNumber: Yup.string().required("Contact number is required"),
  phone: Yup.string()
    .matches(/^[\d\s\-\+\(\)]+$/, "Invalid phone number format")
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  billingAddress: Yup.string().required("Billing address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zipCode: Yup.string()
    .matches(/^\d{5}(-\d{4})?$/, "Invalid zip code format")
    .required("Zip code is required"),
  accountsPayableEmail: Yup.string()
    .email("Invalid email")
    .required("Accounts payable email is required"),
  notes: Yup.string(),
});

const baseInitialValues = {
  clientNickname: "",
  company: "",
  contactNumber: "",
  phone: "",
  email: "",
  billingAddress: "",
  city: "",
  state: "",
  zipCode: "",
  accountsPayableEmail: "",
  notes: "",
};

function ClientDetails() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientData, setClientData] = useState(baseInitialValues);
  const [projects, setProjects] = useState([]);

  const loadClient = async () => {
    setLoading(true);
    try {
      const res = await ClientApi.getClientById(clientId);
      const payload = res?.data?.data?.client || res?.data?.data || res?.data || {};
      setClientData({
        clientNickname: payload.clientNickname || "",
        company: payload.company || "",
        contactNumber: payload.contactNumber || payload.contactName || "",
        phone: payload.phone || "",
        email: payload.email || "",
        billingAddress: payload.billingAddress || "",
        city: payload.city || "",
        state: payload.state || "",
        zipCode: payload.zipCode || "",
        accountsPayableEmail: payload.accountsPayableEmail || "",
        notes: payload.notes || "",
      });
      const fetchedProjects = payload.projects || payload.projectList || [];
      setProjects(Array.isArray(fetchedProjects) ? fetchedProjects : []);
    } catch (error) {
      console.error("Error fetching client:", error);
      toast.error("Failed to load client");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClient();
  }, [clientId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: clientData,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSaving(true);
        await ClientApi.updateClient(clientId, values);
        toast.success("Client updated successfully");
        await loadClient();
      } catch (error) {
        console.error("Error updating client:", error);
        toast.error(error.response?.data?.message || "Update failed");
      } finally {
        setSaving(false);
      }
    },
  });

  const formattedProjects = useMemo(() => {
    return projects.map((project) => ({
      id: project.id,
      name: project.name || project.projectName || "Untitled",
      description: project.description || "-",
      contractAmount: project.totalContractAmount || 0,
         
    }));
  }, [projects]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: "0 auto", padding: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          Client Details
        </Typography>
        <Button variant="text" onClick={() => navigate("/admin/clients")}>
          Back to Clients
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Client Information
        </Typography>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {[
              { label: "Client Nickname", name: "clientNickname", required: true },
              { label: "Company", name: "company", required: true },
              { label: "Contact Number", name: "contactNumber", required: true },
              { label: "Phone", name: "phone", required: true },
              { label: "Email", name: "email", type: "email", required: true },
              { label: "Accounts Payable Email", name: "accountsPayableEmail", type: "email", required: true },
              { label: "Billing Address", name: "billingAddress", required: true },
              { label: "City", name: "city", required: true },
              { label: "State", name: "state", required: true },
              { label: "Zip Code", name: "zipCode", required: true },
            ].map((field) => (
              <Grid item xs={12} md={6} key={field.name}>
                <Typography variant="subtitle2" gutterBottom>
                  {field.label}
                  {field.required ? " *" : ""}
                </Typography>
                <TextField
                  fullWidth
                  id={field.name}
                  name={field.name}
                  type={field.type || "text"}
                  value={formik.values[field.name]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                  helperText={formik.touched[field.name] && formik.errors[field.name]}
                />
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                id="notes"
                name="notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => formik.resetForm()}
                  disabled={saving}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Projects
        </Typography>
        {formattedProjects.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No projects associated with this client yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Contract Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formattedProjects.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>{project.name}</TableCell>
                    <TableCell>{project.description}</TableCell>
                    <TableCell align="right">
                      {project.contractAmount.toLocaleString(undefined, {
                        style: "currency",
                        currency: "USD",
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default ClientDetails;

